/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { FiChevronRight, FiFolder } from "react-icons/fi";
import { Breadcrumbs } from "baseui/breadcrumbs";
import { Button, KIND, SIZE } from "baseui/button";
import { Block } from "baseui/block";
import { LabelMedium } from "baseui/typography";
import { Folder } from "Api";
import { UUID } from "../Utils/Types";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { DexterModal } from "../Shared/DexterModal";

/** One selected row being moved: a file or a folder, and where it lives now. */
export interface MoveToItem {
  id: string;
  kind: "file" | "folder";
  /** The folder it's in now; null = root ("Files") */
  parentId: string | null;
}

export interface MoveToModalProps {
  isOpen: boolean;
  confirmLabel?: string;
  /** Everything selected — files, folders, or a mix */
  items: MoveToItem[];
  /** Every folder, flat — the picker browses them by parentId */
  folders: Folder[];
  /** Called with the destination folder id, or null for the root */
  onConfirm: (destinationId: UUID | null) => void;
  /** Current level. If null then starts at root Files level */
  startFolderId: UUID | null;
  onCancel: () => void;
}

interface FolderCrumb {
  id: UUID;
  name: string;
}

const ROOT_NAME = "Files";

/** Every folder keyed by its id, for parent lookups. */
const foldersById = (folders: Folder[]): Map<string, Folder> =>
  new Map(folders.map((folder) => [String(folder.id), folder]));

/**
 * The crumbs from the top-level folder down to folderId, root first. Walks up
 * through parentId and stops at a missing parent or a cycle.
 */
const folderCrumbs = (
  folderId: UUID,
  byId: Map<string, Folder>
): FolderCrumb[] => {
  const crumbs: FolderCrumb[] = [];
  const seen = new Set<string>();
  let current = byId.get(String(folderId));

  while (current && !seen.has(String(current.id))) {
    seen.add(String(current.id));
    crumbs.unshift({ id: current.id as UUID, name: current.name });
    current = current.parentId ? byId.get(String(current.parentId)) : undefined;
  }
  return crumbs;
};

/**
 * "Move to" picker: browse from the root through a breadcrumb header, one level
 * at a time, and move into whichever level is open. Selected folders are hidden,
 * so a folder can't be moved into itself or any of its subfolders, and Move is
 * disabled at the level the selection is already in.
 */
export const MoveToModal: React.FC<MoveToModalProps> = ({
  isOpen,
  confirmLabel = "Move",
  items,
  folders,
  onConfirm,
  startFolderId,
  onCancel,
}) => {
  const { currentTheme } = useDexterTheme();
  const { colors, fonts, fontSizes } = currentTheme;

  // The level being viewed; null = the "Files" root.
  const [browseId, setBrowseId] = React.useState<UUID | null>(startFolderId);

  // Every opening starts at the root.
  React.useEffect(() => {
    if (isOpen) setBrowseId(startFolderId);
  }, [isOpen, startFolderId]);

  const byId = React.useMemo(() => foldersById(folders), [folders]);

  // If the open folder no longer exists, fall back to the root.
  const location: UUID | null =
    browseId !== null && byId.has(String(browseId)) ? browseId : null;

  const crumbs = React.useMemo(
    () => (location ? folderCrumbs(location, byId) : []),
    [location, byId]
  );

  // The folders being moved. Hiding them also hides everything inside them.
  const excludedIds = React.useMemo(
    () =>
      new Set(
        items
          .filter((item) => item.kind === "folder")
          .map((item) => String(item.id))
      ),
    [items]
  );

  // Folders directly inside the current level.
  const childFolders = React.useMemo(
    () =>
      folders.filter(
        (folder) =>
          (folder.parentId ? String(folder.parentId) : null) ===
          (location ? String(location) : null)
      ),
    [folders, location]
  );

  const availableFolders = React.useMemo(
    () =>
      childFolders
        .filter((folder) => !excludedIds.has(String(folder.id)))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [childFolders, excludedIds]
  );

  // Moving here would change nothing: every selected item is already at this level.
  const isCurrentLocation =
    items.length > 0 &&
    items.every(
      (item) =>
        (item.parentId ? String(item.parentId) : null) ===
        (location ? String(location) : null)
    );

  // ── Breadcrumb header ──────────────────────────────────────────────────────
  const crumbTextStyle = {
    fontFamily: fonts.primary,
    fontSize: fontSizes.large,
    lineHeight: "normal",
  };

  const renderCrumb = (
    key: string,
    label: string,
    isCurrent: boolean,
    onClick: () => void
  ): JSX.Element =>
    isCurrent ? (
      <LabelMedium
        key={key}
        overrides={{
          Block: {
            props: { "aria-current": "location" },
            style: {
              ...crumbTextStyle,
              color: colors.defaultTextColor,
              fontWeight: 600,
            },
          },
        }}
      >
        {label}
      </LabelMedium>
    ) : (
      <Button
        key={key}
        kind={KIND.tertiary}
        size={SIZE.compact}
        onClick={onClick}
        overrides={{
          BaseButton: {
            style: {
              ...crumbTextStyle,
              color: colors.defaultTextColor,
              fontWeight: 400,
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 0,
              paddingRight: 0,
              backgroundColor: "transparent",
              ":hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
              ":active": { backgroundColor: "transparent" },
            },
          },
        }}
      >
        {label}
      </Button>
    );

  const breadcrumbHeader = (
    <Breadcrumbs
      overrides={{
        Root: { props: { "aria-label": "Move destination" } },
        List: {
          style: { display: "flex", alignItems: "center", flexWrap: "wrap" },
        },
        Separator: { style: { color: colors.secondaryTextColor } },
      }}
    >
      {renderCrumb("root", ROOT_NAME, location === null, () =>
        setBrowseId(null)
      )}
      {crumbs.map((crumb, index) =>
        renderCrumb(crumb.id, crumb.name, index === crumbs.length - 1, () =>
          setBrowseId(crumb.id)
        )
      )}
    </Breadcrumbs>
  );

  // At the root with nowhere to go: no folders to open, and the selection is already here.
  const nowhereToMove =
    location === null && availableFolders.length === 0 && isCurrentLocation;

  const emptyMessage = nowhereToMove
    ? "No locations to move files or folders to. Close the modal and create a new folder to move to."
    : childFolders.length === 0 && "No more folders to move to";

  return (
    <DexterModal
      isOpen={isOpen}
      title={breadcrumbHeader}
      confirmLabel={confirmLabel}
      confirmDisabled={isCurrentLocation}
      onConfirm={() => onConfirm(location)}
      onCancel={onCancel}
    >
      <Block
        display="flex"
        flexDirection="column"
        overrides={{
          Block: {
            style: {
              rowGap: "2px",
              // Long folder lists scroll inside the modal
              maxHeight: "min(360px, 50vh)",
              overflowY: "auto",
            },
          },
        }}
      >
        {availableFolders.length === 0 && (
          <LabelMedium
            overrides={{
              Block: {
                style: {
                  color: colors.secondaryTextColor,
                  fontFamily: fonts.primary,
                  fontSize: fontSizes.medium,
                  fontWeight: 400,
                  lineHeight: "normal",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  paddingLeft: "12px",
                  paddingRight: "12px",
                },
              },
            }}
          >
            {emptyMessage}
          </LabelMedium>
        )}

        {availableFolders.map((folder) => (
          <Button
            key={String(folder.id)}
            kind={KIND.tertiary}
            size={SIZE.compact}
            aria-label={`Open ${folder.name}`}
            onClick={() => setBrowseId(folder.id as UUID)}
            startEnhancer={
              <FiFolder
                size={18}
                color={colors.primaryLight}
                aria-hidden="true"
              />
            }
            endEnhancer={
              <FiChevronRight
                size={16}
                color={colors.secondaryTextColor}
                aria-hidden="true"
              />
            }
            overrides={{
              BaseButton: {
                style: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "100%",
                  minHeight: "42px",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  paddingLeft: "12px",
                  paddingRight: "12px",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  textAlign: "left",
                  color: colors.text,
                  fontFamily: fonts.primary,
                  fontSize: fontSizes.medium,
                  fontWeight: 400,
                  lineHeight: "normal",
                  backgroundColor: "transparent",
                  ":hover": {
                    backgroundColor: colors.selectionBackgroundPrimary,
                  },
                  ":active": {
                    backgroundColor: colors.selectionBackgroundPrimary,
                  },
                },
              },
              StartEnhancer: { style: { marginRight: "12px", flexShrink: 0 } },
              // Pushes the chevron to the right edge of the row.
              EndEnhancer: { style: { marginLeft: "auto", flexShrink: 0 } },
            }}
          >
            <Block
              overflow="hidden"
              overrides={{
                Block: {
                  style: { textOverflow: "ellipsis", whiteSpace: "nowrap" },
                },
              }}
            >
              {folder.name}
            </Block>
          </Button>
        ))}
      </Block>
    </DexterModal>
  );
};

export default MoveToModal;
